import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { MdTune } from 'react-icons/md';
import { Button, CardPickerModal, DialogField, DialogFieldLabel, TagPill } from '@aibuild/ui';
import { FieldHint, SkillList, SkillRow } from './AppAgentFields.styled';

interface AppSkillChoiceFieldProps {
  readonly allowedSkills: readonly string[];
  readonly alternativeSkills: readonly string[];
  readonly skillName: (id: string) => string;
  readonly onChange: (alternativeSkills: readonly string[]) => void;
}

/** Which of the app's skills are interchangeable software, of which each run uses the one the person picks. */
const AppSkillChoiceField: React.FC<AppSkillChoiceFieldProps> = ({
  allowedSkills,
  alternativeSkills,
  skillName,
  onChange,
}) => {
  const intl = useIntl();
  const [picking, setPicking] = useState(false);
  const chosen = alternativeSkills.filter((id) => allowedSkills.includes(id));
  const title = intl.formatMessage({
    id: 'apps.builder.details.softwareChoice',
    defaultMessage: 'Software the person running picks from',
  });

  return (
    <DialogField>
      <DialogFieldLabel as="span">{title}</DialogFieldLabel>
      <SkillRow>
        {chosen.length >= 2 ? (
          <SkillList data-testid="app-builder__software-choice">
            {chosen.map((id, index) => (
              <TagPill
                key={id}
                label={
                  index === 0
                    ? intl.formatMessage(
                        { id: 'apps.builder.details.softwareChoice.default', defaultMessage: '{name} (default)' },
                        { name: skillName(id) },
                      )
                    : skillName(id)
                }
                prefix={false}
                size="sm"
              />
            ))}
          </SkillList>
        ) : (
          <FieldHint data-testid="app-builder__software-choice">
            {intl.formatMessage({
              id: 'apps.builder.details.softwareChoice.none',
              defaultMessage: 'None. Every run can use all of the skills above.',
            })}
          </FieldHint>
        )}
        <Button
          variant="outlined"
          size="sm"
          leadingIcon={<MdTune size={16} aria-hidden="true" />}
          onClick={() => setPicking(true)}
          dataTestId="app-builder__software-choice-open"
        >
          {intl.formatMessage({ id: 'apps.builder.details.softwareChoice.choose', defaultMessage: 'Choose' })}
        </Button>
      </SkillRow>
      <CardPickerModal
        open={picking}
        onClose={() => setPicking(false)}
        title={title}
        subtitle={intl.formatMessage({
          id: 'apps.builder.details.softwareChoice.subtitle',
          defaultMessage:
            'Pick two or more skills that do the same job with different software. Each run uses the one the person picks; the first you pick is the default.',
        })}
        options={allowedSkills.map((id) => ({ id, title: skillName(id) }))}
        selectionMode="multiple"
        initialSelected={chosen}
        onConfirm={(selected) => {
          const picked = Array.isArray(selected) ? selected : [];
          onChange(picked.filter((id) => allowedSkills.includes(id)));
        }}
        allowEmpty
        density="compact"
        confirmLabel={intl.formatMessage({ id: 'apps.builder.details.skills.apply', defaultMessage: 'Apply' })}
        cancelLabel={intl.formatMessage({ id: 'apps.builder.details.skills.cancel', defaultMessage: 'Cancel' })}
        dataTestId="app-builder__software-choice-picker"
      />
    </DialogField>
  );
};

export default AppSkillChoiceField;
